// src/components/CommitmentsCard.js
//
// Surfaces active commitments from past conversations on ThisMonth.
// Shows commitment text, days remaining, with check-in actions.
//
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { theme } from '../lib/theme'

const c = theme.colors
const f = theme.fonts

export default function CommitmentsCard() {
  const navigate = useNavigate()
  const [commitments, setCommitments] = useState([])
  const [loading, setLoading] = useState(true)
  const [acting, setActing] = useState(null) // commitment id being acted on

  useEffect(() => { fetchActive() }, [])

  async function fetchActive() {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }

    // Find partnership
    const { data: link } = await supabase.from('partner_links')
      .select('id')
      .or(`user_id_1.eq.${user.id},user_id_2.eq.${user.id}`)
      .eq('is_active', true)
      .maybeSingle()
    if (!link) { setLoading(false); return }

    // Fetch all active commitments for the partnership's conversations
    const { data: convoIds } = await supabase.from('money_conversations')
      .select('id')
      .eq('partnership_id', link.id)
    const ids = (convoIds || []).map(c => c.id)
    if (ids.length === 0) { setLoading(false); return }

    const { data: rows } = await supabase.from('conversation_commitments')
      .select('*')
      .in('conversation_id', ids)
      .eq('status', 'active')
      .order('created_at', { ascending: false })

    // Auto-expire any commitment whose target_date passed > 30 days ago
    const now = new Date()
    const stillActive = []
    const toExpire = []
    for (const com of (rows || [])) {
      if (com.target_date) {
        const target = new Date(com.target_date)
        const daysSinceTarget = Math.floor((now - target) / (1000 * 60 * 60 * 24))
        if (daysSinceTarget > 30) {
          toExpire.push(com.id)
          continue
        }
      }
      stillActive.push(com)
    }
    if (toExpire.length > 0) {
      await supabase.from('conversation_commitments')
        .update({ status: 'expired' })
        .in('id', toExpire)
    }

    setCommitments(stillActive)
    setLoading(false)
  }

  const checkIn = async (commitmentId, status) => {
    setActing(commitmentId)
    const { data: { user } } = await supabase.auth.getUser()
    await supabase.from('conversation_commitments')
      .update({
        status,
        checked_in_by_user_id: user.id,
        checked_in_at: new Date().toISOString(),
        completed: status === 'completed',
        completed_at: status === 'completed' ? new Date().toISOString() : null,
      })
      .eq('id', commitmentId)
    setCommitments(prev => prev.filter(c => c.id !== commitmentId))
    setActing(null)
  }

  if (loading) return null
  if (commitments.length === 0) return null

  return (
    <section style={styles.section}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h2 style={styles.title}>Active commitments</h2>
        </div>
        <button onClick={() => navigate('/checkins')} style={styles.viewLink}>
          From your check-ins →
        </button>
      </div>

      <div style={styles.list}>
        {commitments.map(com => {
          const daysRemaining = com.target_date
            ? Math.ceil((new Date(com.target_date) - new Date()) / (1000 * 60 * 60 * 24))
            : null
          const isOverdue = daysRemaining !== null && daysRemaining < 0
          const isActing = acting === com.id

          return (
            <div key={com.id} style={styles.card}>
              <div style={styles.cardBody}>
                <div style={styles.cardText}>{com.text}</div>
                {daysRemaining !== null && (
                  <div style={styles.cardMeta}>
                    {isOverdue
                      ? `${Math.abs(daysRemaining)} days past target`
                      : daysRemaining === 0
                      ? 'Target date today'
                      : `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} remaining`}
                  </div>
                )}
              </div>
              <div style={styles.cardActions}>
                <button
                  onClick={() => checkIn(com.id, 'completed')}
                  disabled={isActing}
                  style={{
                    ...styles.actionBtn,
                    ...styles.didItBtn,
                    opacity: isActing ? 0.5 : 1,
                  }}
                  title="Mark as done"
                >
                  We did it
                </button>
                <button
                  onClick={() => checkIn(com.id, 'struggled')}
                  disabled={isActing}
                  style={{
                    ...styles.actionBtn,
                    ...styles.struggledBtn,
                    opacity: isActing ? 0.5 : 1,
                  }}
                  title="Mark as struggled"
                >
                  We struggled
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

const styles = {
  section: {
    background: c.greenSoft || '#e8f5e9',
    border: `1px solid ${c.green}`,
    borderRadius: '14px',
    padding: '18px 20px',
    marginBottom: '20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '12px',
  },
  headerLeft: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  title: {
    fontFamily: f.display, fontSize: '15px', fontWeight: 600,
    color: c.plum, margin: 0,
  },
  viewLink: {
    background: 'none', border: 'none',
    color: c.green, fontSize: '12px', fontWeight: 500,
    cursor: 'pointer', padding: 0,
    fontFamily: 'inherit',
  },
  list: {
    display: 'flex', flexDirection: 'column', gap: '10px',
  },
  card: {
    background: c.white,
    borderRadius: '10px',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  cardBody: { display: 'flex', flexDirection: 'column', gap: '4px' },
  cardText: {
    fontSize: '14px',
    color: c.ink,
    lineHeight: 1.45,
  },
  cardMeta: {
    fontSize: '11px', color: c.inkMuted,
  },
  cardActions: {
    display: 'flex', gap: '8px',
  },
  actionBtn: {
    flex: 1,
    border: `1px solid ${c.line}`,
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '12px', fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    background: c.white,
    color: c.plum,
    transition: 'background 100ms',
  },
  didItBtn: {
    borderColor: c.green,
    color: c.green,
  },
  struggledBtn: {
    borderColor: c.line,
    color: c.inkMuted,
  },
}